import * as Sentry from "@sentry/astro";
import type { InstagramData } from "./types";

export default function extractValentine(
  data: InstagramData,
  index: number
): {
  userName: string;
  totalMessages: number;
  longestStreakInDays: number;
  otherPersonReactionPercentage: number;
  mostCommonReaction: string | undefined;
  mostActiveHour: number;
  conversationStarter: string;
} {
  const conversationsInOrder = data.conversations.sort((a, b) => {
    const aLength = a.messages.length;
    const bLength = b.messages.length;

    if (aLength === bLength) {
      return 0;
    }

    return aLength > bLength ? -1 : 1;
  });

  const conversation = conversationsInOrder[index];

  /** STREAK CALCULATION */

  // Sort messages by timestamp
  const sortedMessages = [...conversation.messages].sort(
    (a, b) => a.timestamp_ms - b.timestamp_ms
  );

  // Find longest streak
  let longestStreakStart = 0;
  let longestStreakEnd = 0;
  let currentStreakStart = 0;
  let currentStreakEnd = 0;

  for (let i = 1; i < sortedMessages.length; i++) {
    const timeDiff =
      sortedMessages[i].timestamp_ms - sortedMessages[i - 1].timestamp_ms;
    const hoursDiff = timeDiff / (1000 * 60 * 60);

    if (hoursDiff <= 24) {
      currentStreakEnd = i;
    } else {
      // Check if this streak is longer than our longest
      const currentStreakLength =
        (sortedMessages[currentStreakEnd].timestamp_ms -
          sortedMessages[currentStreakStart].timestamp_ms) /
        (1000 * 60 * 60 * 24);

      const longestStreakLength =
        (sortedMessages[longestStreakEnd].timestamp_ms -
          sortedMessages[longestStreakStart].timestamp_ms) /
        (1000 * 60 * 60 * 24);

      if (currentStreakLength > longestStreakLength) {
        longestStreakStart = currentStreakStart;
        longestStreakEnd = currentStreakEnd;
      }

      // Start new streak
      currentStreakStart = i;
      currentStreakEnd = i;
    }
  }

  // Check final streak
  const currentStreakLength =
    (sortedMessages[currentStreakEnd].timestamp_ms -
      sortedMessages[currentStreakStart].timestamp_ms) /
    (1000 * 60 * 60 * 24);

  const longestStreakLength =
    (sortedMessages[longestStreakEnd].timestamp_ms -
      sortedMessages[longestStreakStart].timestamp_ms) /
    (1000 * 60 * 60 * 24);

  if (currentStreakLength > longestStreakLength) {
    longestStreakStart = currentStreakStart;
    longestStreakEnd = currentStreakEnd;
  }

  // Calculate final streak length and round up to nearest day
  const longestStreakInDays =
    Math.ceil(
      (sortedMessages[longestStreakEnd].timestamp_ms -
        sortedMessages[longestStreakStart].timestamp_ms) /
        (1000 * 60 * 60 * 24)
    ) + 1; // Add 1 because if messages are on same day, we want streak of 1 not 0

  /** REACTION PERCENTAGE */

  const messagesFromOtherPerson = conversation.messages.filter(
    (message) => message.sender_name !== conversation.user_name
  );

  const messagesWithReactions = messagesFromOtherPerson.filter(
    (message) =>
      message.reactions &&
      message.reactions.some(
        (reaction) => reaction.actor === conversation.user_name
      )
  );

  const otherPersonReactionPercentage =
    messagesFromOtherPerson.length > 0
      ? (messagesWithReactions.length / messagesFromOtherPerson.length) * 100
      : 0;

  /** MOST COMMON REACTION */

  const allReactions = conversation.messages
    .flatMap((message) => message.reactions ?? [])
    .filter((reaction) => reaction.actor === conversation.user_name)
    .map((reaction) => reaction.reaction);

  const reactionCounts = allReactions.reduce((acc, reaction) => {
    acc[reaction] = (acc[reaction] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Sort reactions by count in descending order
  const sortedReactions = Object.entries(reactionCounts)
    .sort(([, countA], [, countB]) => countB - countA)
    .map(([reaction]) => reaction);

  let selectedReaction = sortedReactions[0];

  // If the most common reaction is a heart and we have other reactions,
  // use the second most common
  if (
    selectedReaction &&
    // Try to skip over heart emojis
    ["%E2%9D%A4"].includes(escape(selectedReaction)) &&
    sortedReactions.length > 1
  ) {
    selectedReaction = sortedReactions[1];
  }

  const decodedReaction = selectedReaction
    ? decodeURIComponent(escape(selectedReaction))
    : undefined;

  /** MOST ACTIVE HOUR */
  const messagesByHour = new Array(24).fill(0);

  conversation.messages.forEach((message) => {
    // Convert timestamp to local date
    const date = new Date(message.timestamp_ms);
    const hour = date.getHours();
    messagesByHour[hour]++;
  });

  // Find hour with most messages
  let mostActiveHour = 0;
  let maxMessages = 0;

  messagesByHour.forEach((count, hour) => {
    if (count > maxMessages) {
      maxMessages = count;
      mostActiveHour = hour;
    }
  });

  /** CONVERSATION STARTER */
  let userStartedCount = 0;
  let otherPersonStartedCount = 0;

  for (let i = 1; i < sortedMessages.length; i++) {
    const timeDiff =
      sortedMessages[i].timestamp_ms - sortedMessages[i - 1].timestamp_ms;
    const hoursDiff = timeDiff / (1000 * 60 * 60);

    // If there was a gap of 24+ hours
    if (hoursDiff > 24) {
      // Check who sent the message after the gap
      if (sortedMessages[i].sender_name === conversation.user_name) {
        userStartedCount++;
      } else {
        otherPersonStartedCount++;
      }
    }
  }

  // Determine who starts conversations more often
  const conversationStarter =
    userStartedCount > otherPersonStartedCount
      ? conversation.user_name
      : userStartedCount < otherPersonStartedCount
      ? sortedMessages.find((m) => m.sender_name !== conversation.user_name)
          ?.sender_name || "Unknown"
      : "Tied";

  if (conversationStarter === "Unknown") {
    Sentry.captureMessage("Unknown conversation starter");
  }

  return {
    userName: decodeURIComponent(escape(conversation.user_name)),
    totalMessages: conversation.messages.length,
    longestStreakInDays,
    otherPersonReactionPercentage,
    mostCommonReaction: decodedReaction,
    mostActiveHour, // Now returns the most active hour in local timezone (0-23)
    conversationStarter: decodeURIComponent(escape(conversationStarter)),
  };
}
